import { css, styled } from 'styled-components';

import { H2 } from '#/components/Text/H2';
import { Text } from '#/components/Text/Text';
import { colors, cssBreakpoints, fontWeights } from '#/theme/theme';

export const Tick = styled(Text)<{
  $stage?: string;
  $duration?: number;
  $type: 'category' | 'range';
}>`
  font-size: 14px;
  font-weight: ${fontWeights.normal};
  color: ${colors.lightContentGrey};
  display: ${(props) => (props.$type === 'range' ? 'none' : undefined)};

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    display: ${(props) =>
      props.$type === 'range' ? 'inline-block' : undefined};
  }

  ${(props) =>
    props.$duration
      ? `
    transition: transform ${props.$duration / 2000}s ease;
    `
      : undefined}

  ${(props) => {
    if (props.$stage === 'exit') {
      return 'transform: translateX(200px)';
    }

    if (props.$stage === 'from') {
      return `
      transform: translateX(-200px)`;
    }

    return undefined;
  }};

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    font-size: 18px;
  }
`;

export const AxisLabel = styled(H2)`
  position: absolute;
  writing-mode: vertical-lr;
  transform: translateY(-50%) scale(-1);
  left: -30px;
  top: 50%;
  white-space: nowrap;
  font-size: 16px;
  font-weight: ${fontWeights.normal};
  color: ${colors.lightContentGrey};
`;

export const TickContainer = styled.div<{
  $alignToGrid: boolean;
  $chartWidth: number;
  $direction: 'x' | 'y';
  $justifyEnd: boolean;
  $numberOfTicks: number;
  $offset: number[];
  $tickSpace: number;
  $hasLabel: boolean;
  $gridLines: number;
  $index: number;
}>`
  display: flex;
  align-items: ${({ $justifyEnd }) => ($justifyEnd ? 'end' : 'baseline')};
  max-width: 160px;
  overflow: hidden;
  position: absolute;
  transition: top 0.25s ease;

  ${(props) => {
    if (props.$direction === 'x') {
      return css`
        position: absolute;
        top: 12px;
        transform: ${props.$alignToGrid ? 'translateX(-50%)' : 'none'};
        left: ${props.$gridLines >= 4
          ? (props.$chartWidth / props.$gridLines / props.$chartWidth) *
            (props.$index * 2) *
            100
          : (props.$chartWidth / props.$gridLines / props.$chartWidth) *
            props.$index *
            100}%;
      `;
    }

    if (props.$direction === 'y') {
      return css`
        height: ${props.$chartWidth / props.$numberOfTicks}px;
        left: ${props.$hasLabel ? 20 : 0}px;
        top: ${props.$gridLines >= 4
          ? (props.$chartWidth / props.$gridLines + props.$tickSpace) *
              (props.$index * 2) -
            props.$offset[0]
          : (props.$chartWidth / props.$gridLines + props.$tickSpace) *
              props.$index -
            props.$offset[0]}px;

        @media all and (min-width: ${cssBreakpoints.smUp}) {
          left: ${props.$hasLabel ? 20 : -44}px;
          top: ${props.$gridLines >= 4
            ? (props.$chartWidth / props.$gridLines + props.$tickSpace) *
                (props.$index * 2) -
              props.$offset[1]
            : (props.$chartWidth / props.$gridLines + props.$tickSpace) *
                props.$index -
              props.$offset[1]}px;
        }

        @media all and (min - width: ${cssBreakpoints.mdUp}) {
          max-width: 170px;
        }
      `;
    }

    return undefined;
  }}
`;

export const TicksContainer = styled.div<{ $type: 'category' | 'range' }>`
  position: relative;

  ${TickContainer} {
    &:first-child > ${Tick}, &:last-child > ${Tick} {
      display: ${(props) =>
        props.$type === 'range' ? 'inline-block' : undefined};
    }
  }
`;
